import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  ClipboardList, User, Target, Calendar, Save, Plus, Loader2, Trash2, Clock,
  Phone, Mail, MessageSquare, MapPin, Video, StickyNote, Users,
} from 'lucide-react';

// Champs qualification + opportunité par catégorie
const CATEGORY_FIELDS = {
  mairie: {
    contactLabel: 'Commune',
    qualification: [
      { key: 'corps_metier_marches', label: 'Corps de métier avec marchés / entreprises référencées', textarea: true },
      { key: 'corps_metier_ouverts', label: 'Corps de métier potentiellement ouverts à la consultation', textarea: true },
      { key: 'commentaire',          label: 'Commentaire important',                                    textarea: true },
    ],
    opportunite: [
      { key: 'besoin_actuel',         label: 'Besoin actuel identifié',                textarea: true },
      { key: 'batiment_interessant',  label: 'Bâtiment potentiellement intéressant',   textarea: true },
      { key: 'interet_carnet_sante',  label: 'Intérêt pour le Carnet de Santé',        options: ['—', 'Oui', 'Non', 'À confirmer'] },
    ],
  },
  syndic: {
    contactLabel: 'Cabinet',
    qualification: [
      { key: 'nb_associes',          label: 'Nombre de collaborateurs' },
      { key: 'volume_dossiers_mois', label: 'Volume copropriétés gérées' },
      { key: 'specialites',          label: 'Spécialités (petites/grandes copro, standing)', textarea: true },
      { key: 'partenaires_actuels',  label: 'Partenaires travaux actuels',              textarea: true },
      { key: 'commentaire',          label: 'Commentaire important',                    textarea: true },
    ],
    opportunite: [
      { key: 'dossier_en_cours',            label: 'Copropriété en cours de travaux',       textarea: true },
      { key: 'type_bien',                   label: 'Type de copropriété',                   options: ['—', 'Petite (<20 lots)', 'Moyenne (20-50 lots)', 'Grande (>50 lots)', 'Autre'] },
      { key: 'interet_convention_apport',   label: 'Intérêt convention apport',             options: ['—', 'Oui', 'Non', 'À confirmer'] },
    ],
  },
  architecte: {
    contactLabel: 'Cabinet',
    qualification: [
      { key: 'effectif',              label: 'Effectif' },
      { key: 'types_projets',         label: 'Types de projets (résid./tertiaire/public)', textarea: true },
      { key: 'partenaires_execution', label: 'Partenaires exécution actuels',              textarea: true },
      { key: 'certifications',        label: 'Certifications recherchées (RGE/Qualibat)',  textarea: true },
      { key: 'commentaire',           label: 'Commentaire important',                       textarea: true },
    ],
    opportunite: [
      { key: 'projet_en_cours',       label: 'Projet en cours cherchant entreprise', textarea: true },
      { key: 'adresse_chantier',      label: 'Adresse / nature du chantier',         textarea: true },
      { key: 'interet_partenariat',   label: 'Intérêt partenariat exclusif corps d\'état', options: ['—', 'Oui', 'Non', 'À confirmer'] },
    ],
  },
  agence: {
    contactLabel: 'Agence',
    qualification: [
      { key: 'reseau',                label: 'Réseau',                              options: ['—', 'Indépendant', 'Franchise'] },
      { key: 'nb_biens_geres',        label: 'Nombre de biens gérés' },
      { key: 'activites',             label: 'Activités (transaction/gestion/syndic)', textarea: true },
      { key: 'partenaires_actuels',   label: 'Partenaires travaux actuels',            textarea: true },
      { key: 'commentaire',           label: 'Commentaire important',                  textarea: true },
    ],
    opportunite: [
      { key: 'biens_a_renover',           label: 'Biens à rénover avant vente/location', textarea: true },
      { key: 'adresses',                  label: 'Adresses',                             textarea: true },
      { key: 'interet_convention_apport', label: 'Intérêt convention apport (%)',        options: ['—', 'Oui', 'Non', 'À confirmer'] },
    ],
  },
};

const JOURNAL_TYPE_META = {
  conversation: { label: 'Conversation', icon: MessageSquare, color: 'text-slate-600 bg-slate-100 border-slate-200' },
  appel:        { label: 'Appel',        icon: Phone,         color: 'text-blue-700 bg-blue-50 border-blue-200' },
  rdv:          { label: 'RDV',          icon: Calendar,      color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  visite:       { label: 'Visite',       icon: MapPin,        color: 'text-amber-700 bg-amber-50 border-amber-200' },
  email:        { label: 'Email',        icon: Mail,          color: 'text-violet-700 bg-violet-50 border-violet-200' },
  note:         { label: 'Note',         icon: StickyNote,    color: 'text-slate-600 bg-slate-100 border-slate-200' },
};

export default function ProspectDossierPanel({ prospectId, category, nomSociete }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dossier, setDossier] = useState({ contact: {}, qualification: {}, opportunite: {}, suivi: {} });
  const [journal, setJournal] = useState([]);
  const [showAdd, setShowAdd] = useState(false);

  const cfg = CATEGORY_FIELDS[category] || CATEGORY_FIELDS.mairie;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/prospects/${prospectId}/dossier`);
      const d = r.data.dossier || {};
      setDossier({
        contact: d.contact || {},
        qualification: d.qualification || {},
        opportunite: d.opportunite || {},
        suivi: d.suivi || {},
      });
      setJournal(r.data.journal || []);
    } catch (e) {
      toast.error('Impossible de charger le dossier');
    }
    setLoading(false);
  }, [prospectId]);

  useEffect(() => { load(); }, [load]);

  const patchBlock = (block, k, v) => {
    setDossier(prev => ({ ...prev, [block]: { ...(prev[block] || {}), [k]: v } }));
  };

  const saveDossier = async () => {
    setSaving(true);
    try {
      await api.put(`/prospects/${prospectId}/dossier`, dossier);
      toast.success('Dossier commercial enregistré');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de l\'enregistrement');
    }
    setSaving(false);
  };

  const addEntry = async (entry) => {
    try {
      const r = await api.post(`/prospects/${prospectId}/journal`, entry);
      setJournal(prev => [r.data.entry, ...prev]);
      setShowAdd(false);
      toast.success('Entrée ajoutée au journal');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec');
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Supprimer cette entrée du journal ?')) return;
    try {
      await api.delete(`/prospects/${prospectId}/journal/${entryId}`);
      setJournal(prev => prev.filter(e => e.entry_id !== entryId));
      toast.success('Entrée supprimée');
    } catch (e) {
      toast.error('Échec');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="prospect-dossier-panel">
      {/* Header + Save */}
      <div className="bg-gradient-to-br from-[#1C49A6] via-[#163C89] to-[#004AAD] rounded-2xl p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-white/60 font-semibold">Dossier commercial</p>
            <h3 className="text-lg font-bold text-white">{nomSociete}</h3>
          </div>
        </div>
        <Button onClick={saveDossier} disabled={saving} className="bg-white text-[#1C49A6] hover:bg-white/90" data-testid="dossier-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer le dossier
        </Button>
      </div>

      {/* 4 blocs */}
      <div className="grid md:grid-cols-2 gap-4">
        <BlockCard title="Contact" icon={User} testid="block-contact">
          <FieldRow label={cfg.contactLabel}>
            <Input value={dossier.contact.entite || ''} onChange={e => patchBlock('contact', 'entite', e.target.value)} data-testid="contact-entite" />
          </FieldRow>
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Nom"><Input value={dossier.contact.nom || ''} onChange={e => patchBlock('contact', 'nom', e.target.value)} data-testid="contact-nom" /></FieldRow>
            <FieldRow label="Prénom"><Input value={dossier.contact.prenom || ''} onChange={e => patchBlock('contact', 'prenom', e.target.value)} data-testid="contact-prenom" /></FieldRow>
          </div>
          <FieldRow label="Fonction"><Input value={dossier.contact.fonction || ''} onChange={e => patchBlock('contact', 'fonction', e.target.value)} data-testid="contact-fonction" /></FieldRow>
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Téléphone"><Input value={dossier.contact.telephone || ''} onChange={e => patchBlock('contact', 'telephone', e.target.value)} data-testid="contact-telephone" /></FieldRow>
            <FieldRow label="Email"><Input type="email" value={dossier.contact.email || ''} onChange={e => patchBlock('contact', 'email', e.target.value)} data-testid="contact-email" /></FieldRow>
          </div>
        </BlockCard>

        <BlockCard title="Qualification" icon={ClipboardList} testid="block-qualification">
          {cfg.qualification.map(f => (
            <FieldRow key={f.key} label={f.label}>
              {f.options ? (
                <select value={dossier.qualification[f.key] || ''} onChange={e => patchBlock('qualification', f.key, e.target.value)}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" data-testid={`qual-${f.key}`}>
                  {f.options.map(o => <option key={o} value={o === '—' ? '' : o}>{o}</option>)}
                </select>
              ) : f.textarea ? (
                <textarea rows={2} value={dossier.qualification[f.key] || ''} onChange={e => patchBlock('qualification', f.key, e.target.value)}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm resize-y" data-testid={`qual-${f.key}`} />
              ) : (
                <Input value={dossier.qualification[f.key] || ''} onChange={e => patchBlock('qualification', f.key, e.target.value)} data-testid={`qual-${f.key}`} />
              )}
            </FieldRow>
          ))}
        </BlockCard>

        <BlockCard title="Opportunité" icon={Target} testid="block-opportunite">
          {cfg.opportunite.map(f => (
            <FieldRow key={f.key} label={f.label}>
              {f.options ? (
                <select value={dossier.opportunite[f.key] || ''} onChange={e => patchBlock('opportunite', f.key, e.target.value)}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" data-testid={`opp-${f.key}`}>
                  {f.options.map(o => <option key={o} value={o === '—' ? '' : o}>{o}</option>)}
                </select>
              ) : f.textarea ? (
                <textarea rows={2} value={dossier.opportunite[f.key] || ''} onChange={e => patchBlock('opportunite', f.key, e.target.value)}
                  className="w-full border border-slate-200 rounded-md p-2 text-sm resize-y" data-testid={`opp-${f.key}`} />
              ) : (
                <Input value={dossier.opportunite[f.key] || ''} onChange={e => patchBlock('opportunite', f.key, e.target.value)} data-testid={`opp-${f.key}`} />
              )}
            </FieldRow>
          ))}
        </BlockCard>

        <BlockCard title="Suivi" icon={Calendar} testid="block-suivi">
          <FieldRow label="RDV obtenu">
            <select value={dossier.suivi.rdv_obtenu === true ? 'oui' : dossier.suivi.rdv_obtenu === false ? 'non' : ''}
              onChange={e => patchBlock('suivi', 'rdv_obtenu', e.target.value === 'oui' ? true : e.target.value === 'non' ? false : null)}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="suivi-rdv-obtenu">
              <option value="">—</option>
              <option value="oui">Oui</option>
              <option value="non">Non</option>
            </select>
          </FieldRow>
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Date"><Input type="date" data-testid="suivi-rdv-date" value={dossier.suivi.rdv_date || ''} onChange={e => patchBlock('suivi', 'rdv_date', e.target.value)} /></FieldRow>
            <FieldRow label="Heure"><Input type="time" data-testid="suivi-rdv-heure" value={dossier.suivi.rdv_heure || ''} onChange={e => patchBlock('suivi', 'rdv_heure', e.target.value)} /></FieldRow>
          </div>
          <FieldRow label="Type de RDV">
            <div className="flex gap-2">
              {['physique', 'visio'].map(t => {
                const active = dossier.suivi.rdv_type === t;
                const Icon = t === 'visio' ? Video : MapPin;
                return (
                  <button key={t} type="button" onClick={() => patchBlock('suivi', 'rdv_type', t)}
                    className={`flex-1 px-3 py-2 text-sm rounded-md border flex items-center justify-center gap-2 transition-colors ${
                      active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                    }`}>
                    <Icon className="w-4 h-4" /> {t === 'visio' ? 'Visio' : 'Physique'}
                  </button>
                );
              })}
            </div>
          </FieldRow>
          <FieldRow label={dossier.suivi.rdv_type === 'visio' ? 'Lien de la visio' : 'Adresse du RDV'}>
            <Input value={dossier.suivi.rdv_lieu || ''} onChange={e => patchBlock('suivi', 'rdv_lieu', e.target.value)} />
          </FieldRow>
          <FieldRow label="Personne Reno Resolve qui prend le relais">
            <Input value={dossier.suivi.rr_relais || ''} onChange={e => patchBlock('suivi', 'rr_relais', e.target.value)} />
          </FieldRow>
          <FieldRow label="Prochaine action">
            <textarea rows={2} value={dossier.suivi.prochaine_action || ''} onChange={e => patchBlock('suivi', 'prochaine_action', e.target.value)}
              className="w-full border border-slate-200 rounded-md p-2 text-sm resize-y" data-testid="suivi-prochaine-action" />
          </FieldRow>
        </BlockCard>
      </div>

      {/* Journal */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="prospect-journal">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-500" />
            <h4 className="font-semibold text-slate-900">Journal des échanges <span className="text-slate-400 font-normal">({journal.length})</span></h4>
          </div>
          <Button size="sm" onClick={() => setShowAdd(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="journal-add-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nouvelle entrée
          </Button>
        </div>

        {showAdd && <AddJournalEntry onCancel={() => setShowAdd(false)} onSubmit={addEntry} />}

        {journal.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">
            Aucune entrée pour l&apos;instant. Après chaque conversation ou RDV, consignez ici un résumé horodaté.
          </div>
        ) : (
          <ol className="divide-y divide-slate-100">
            {journal.map(e => <JournalRow key={e.entry_id} entry={e} onDelete={() => deleteEntry(e.entry_id)} />)}
          </ol>
        )}
      </div>
    </div>
  );
}

function BlockCard({ title, icon: Icon, children, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid={testid}>
      <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
        <Icon className="w-4 h-4 text-slate-500" />
        <h4 className="text-xs font-bold uppercase tracking-widest text-slate-700">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function JournalRow({ entry, onDelete }) {
  const meta = JOURNAL_TYPE_META[entry.type] || JOURNAL_TYPE_META.note;
  const Icon = meta.icon;
  const dt = entry.date ? new Date(entry.date) : null;
  const dateStr = dt ? dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  const timeStr = dt ? dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <li className="px-5 py-4 hover:bg-slate-50 group flex gap-3" data-testid={`journal-entry-${entry.entry_id}`}>
      <div className={`w-9 h-9 shrink-0 rounded-lg border ${meta.color} flex items-center justify-center`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${meta.color}`}>{meta.label}</span>
          <span className="text-xs text-slate-500">{dateStr} · {timeStr}</span>
          {entry.author_name && <span className="text-xs text-slate-400">par {entry.author_name}</span>}
          {entry.duree_min ? <span className="text-xs text-slate-400">· {entry.duree_min} min</span> : null}
        </div>
        <p className="text-sm font-medium text-slate-900 mt-0.5">{entry.resume}</p>
        {entry.interlocuteur && <p className="text-xs text-slate-500 mt-0.5"><Users className="w-3 h-3 inline mr-1" />{entry.interlocuteur}</p>}
        {entry.contenu && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{entry.contenu}</p>}
        {entry.prochaine_action && (
          <p className="text-xs text-blue-700 mt-2 bg-blue-50 border border-blue-100 rounded-md px-2 py-1 inline-block">
            → {entry.prochaine_action}
          </p>
        )}
      </div>
      <button onClick={onDelete} className="opacity-0 group-hover:opacity-100 p-1 text-slate-300 hover:text-red-500 self-start"
        data-testid={`journal-delete-${entry.entry_id}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </li>
  );
}

function AddJournalEntry({ onCancel, onSubmit }) {
  const [form, setForm] = useState({
    type: 'conversation',
    date: new Date().toISOString().slice(0, 16),
    resume: '',
    contenu: '',
    interlocuteur: '',
    duree_min: '',
    prochaine_action: '',
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.resume.trim()) { toast.error('Le résumé est requis'); return; }
    setSaving(true);
    const payload = {
      ...form,
      date: form.date ? new Date(form.date).toISOString() : null,
      duree_min: form.duree_min ? parseInt(form.duree_min, 10) : null,
    };
    await onSubmit(payload);
    setSaving(false);
  };
  return (
    <div className="px-5 py-4 bg-slate-50 border-b border-slate-100 space-y-3" data-testid="journal-add-form">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <FieldRow label="Type">
          <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" data-testid="journal-type">
            {Object.entries(JOURNAL_TYPE_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
        </FieldRow>
        <FieldRow label="Date & heure">
          <Input type="datetime-local" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
        </FieldRow>
        <FieldRow label="Interlocuteur">
          <Input value={form.interlocuteur} onChange={e => setForm({ ...form, interlocuteur: e.target.value })} placeholder="Ex : Marie Dupont (DGS)" />
        </FieldRow>
        <FieldRow label="Durée (min)">
          <Input type="number" value={form.duree_min} onChange={e => setForm({ ...form, duree_min: e.target.value })} />
        </FieldRow>
      </div>
      <FieldRow label="Résumé *">
        <Input value={form.resume} onChange={e => setForm({ ...form, resume: e.target.value })} placeholder="Sujet de l'échange" data-testid="journal-resume" />
      </FieldRow>
      <FieldRow label="Notes détaillées">
        <textarea rows={3} value={form.contenu} onChange={e => setForm({ ...form, contenu: e.target.value })}
          className="w-full border border-slate-200 rounded-md p-2 text-sm resize-y" placeholder="Compte-rendu…" />
      </FieldRow>
      <FieldRow label="Prochaine action décidée">
        <Input value={form.prochaine_action} onChange={e => setForm({ ...form, prochaine_action: e.target.value })} placeholder="Ex : Envoyer devis avant vendredi" />
      </FieldRow>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>Annuler</Button>
        <Button size="sm" onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="journal-submit">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
          Ajouter
        </Button>
      </div>
    </div>
  );
}
